/**
 * PHOTOS AND FILES AS AN ANSWER (services field fixes G2.4). PURE: no Vue, no store, no I/O.
 *
 * A question of kind `photo` or `file` takes what the customer sends. Its owner sets two limits,
 * `uploads: { maxFiles, maxMb }`, and both are Meta's to bound: at most 10 files in one form
 * response, 25 MB a file, and 100 MB in all. The sidecar's schema refuses anything past them
 * (`services/ai-sidecar/src/tasks/uploads.ts` holds the same numbers); this file only ever
 * OFFERS choices inside them, so the screen cannot build a step the save refuses.
 */

export const UPLOAD_KINDS = Object.freeze(['photo', 'file']);

/** The owner's defaults (spec G2.4): three files, five megabytes each. */
export const DEFAULT_MAX_FILES = 3;
export const DEFAULT_MAX_MB = 5;
export const MAX_UPLOAD_FILES = 10;
export const MAX_UPLOAD_MB = 25;
export const MAX_UPLOAD_TOTAL_MB = 100;

/** The sizes offered, in megabytes. Each one Meta takes for one file. */
export const MB_CHOICES = Object.freeze([1, 2, 5, 10, 16, 25]);

export const isUploadKind = kind => UPLOAD_KINDS.includes(kind);

/** Whether this step is a photo or file question. */
export const isUploadStep = step =>
  step?.type === 'ask' && isUploadKind(step?.kind);

const whole = (value, fallback) =>
  Number.isInteger(value) && value > 0 ? value : fallback;

/** The step's limits with the defaults filled in, as numbers. */
export const uploadLimitsOf = step => ({
  maxFiles: whole(step?.uploads?.maxFiles, DEFAULT_MAX_FILES),
  maxMb: whole(step?.uploads?.maxMb, DEFAULT_MAX_MB),
});

/** How many files the owner may allow at a size, inside Meta's 100 MB. */
export const fileChoices = maxMb =>
  Array.from({ length: MAX_UPLOAD_FILES }, (_, i) => i + 1).filter(
    count => count * maxMb <= MAX_UPLOAD_TOTAL_MB
  );

/** The sizes the owner may allow for a count, inside Meta's 100 MB. */
export const mbChoices = maxFiles =>
  MB_CHOICES.filter(mb => mb * maxFiles <= MAX_UPLOAD_TOTAL_MB);

/**
 * The step with one limit changed. A limit back at its default is left out, so a step the
 * owner never touched stays byte for byte what it was, and an empty `uploads` is removed.
 */
export const withUploadLimit = (step, patch) => {
  const limits = { ...uploadLimitsOf(step), ...patch };
  const uploads = {};
  if (limits.maxFiles !== DEFAULT_MAX_FILES) uploads.maxFiles = limits.maxFiles;
  if (limits.maxMb !== DEFAULT_MAX_MB) uploads.maxMb = limits.maxMb;
  const next = { ...step };
  if (Object.keys(uploads).length) next.uploads = uploads;
  else delete next.uploads;
  return next;
};

/**
 * A photo or file is links, never a contact's name, email or phone (the sidecar refuses one,
 * `upload_saves_to_contact`). A step that becomes one keeps its chat key and loses the contact
 * field; a step with no chat key is left for the save to say so.
 */
export const withoutContactSave = step => {
  if (!step?.saveTo?.contact || !step.saveTo.chat) return step;
  return { ...step, saveTo: { chat: step.saveTo.chat } };
};
